import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputFieldModule } from '@xt/ui-kit';
import { TranslocoModule } from '@jsverse/transloco';
import { MatExpansionModule } from '@angular/material/expansion';
import { ProviderSearchParams } from '@core/data/providers/base-provider.model';
import { DropdownModule } from '@shared/components/atoms/dropdown/dropdown.module';
import { ButtonModule } from '@shared/components/atoms/button/button.module';
import { UserService } from '@core/services/user.service';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { MatIconModule } from '@angular/material/icon';
import { CategoryElement } from '@core/data/partial/categories.model';
import { FilterField } from '@shared/models/filter-field.model';
import { isFilterFieldDropdown } from '@shared/helpers/is-field-dropdown';
import { FilterInputWidth } from '@shared/models/filter-input-width.model';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'xt-portal-filter-provider-form',
  templateUrl: './filter-provider-form.component.html',
  styleUrls: ['./filter-provider-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    InputFieldModule,
    MatExpansionModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoModule,
    DropdownModule,
    ButtonModule,
    CypressDirectiveModule,
    MatIconModule,
  ],
})
export class FilterProviderForm implements OnInit {
  @Input() filterFields: {
    standard: FilterField[];
    advanced?: FilterField[];
  };

  @Input() titleLabel: boolean = true;
  @Input() isReconciliation: boolean = false;
  @Input() addProvider: boolean = false;

  @Output() searchHandler = new EventEmitter();
  @Output() resetHandler = new EventEmitter();

  searchForm: FormGroup;
  isSearchEnabled: boolean = false;
  dropdownValue: string;
  mandantDropdown: string | null;
  mandantId: string;
  hasMultipleMandates: boolean = false;

  public isFilterFieldDropdown = isFilterFieldDropdown;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  get descriptionKey(): string {
    return this.isReconciliation
      ? 'app.reconciliation.facilityDescription'
      : 'app.privacyCockpit.description';
  }

  public getInputWidth(field: FilterField): FilterInputWidth {
    if (isFilterFieldDropdown(field)) return 'normal';
    return field.width ?? 'normal';
  }

  public getDropdownValueForField(fieldName: string): string {
    return fieldName === 'mandantId'
      ? this.mandantDropdown
      : this.dropdownValue;
  }

  public shouldHideDropdown(
    field: FilterField,
    entries: CategoryElement[],
  ): boolean {
    if (!isFilterFieldDropdown(field)) return true;
    return field.displayed === false || !entries.length;
  }

  public getPreselectFirst(field: FilterField): boolean {
    if (!isFilterFieldDropdown(field)) return false;
    return field.preselectFirst || false;
  }

  initForm() {
    const controlsConfig = {};
    const filterFieldsList = [
      ...(this.filterFields?.advanced || []),
      ...(this.filterFields?.standard || []),
    ];

    filterFieldsList.forEach(({ name, required }) => {
      controlsConfig[name] = [null];
      if (required && this.addProvider) {
        controlsConfig[name].push(Validators.required);
      }
    });

    this.searchForm = this.fb.group(controlsConfig);

    this.isSearchEnabled = this.searchForm.valid;

    this.searchForm.statusChanges.subscribe(() => {
      this.isSearchEnabled = this.searchForm.valid;
    });

    // initiate if predefinedFirst
    filterFieldsList.forEach((filterField) => {
      if (isFilterFieldDropdown(filterField) && filterField.preselectFirst) {
        filterField.entries$.subscribe((res) => {
          const firstEntry = res[0];
          if (res.length > 1) {
            this.hasMultipleMandates = true;
          } else {
            this.mandantId = firstEntry?.id;
            this.mandantDropdown = firstEntry?.name;
          }
          if (firstEntry?.id) {
            this.searchForm.controls[filterField.name].setValue(firstEntry.id);
          }
        });
      }
    });
    if (this.addProvider) {
      this.searchForm.controls['zip']?.markAsTouched();
      this.searchForm.controls['city']?.markAsTouched();
    }
  }

  onSelectDropdown(e, field) {
    if (field === 'mandantId') {
      this.mandantDropdown = e;
    } else {
      this.dropdownValue = e;
    }
    this.searchForm.controls[field].setValue(e.id);
  }

  resetFilter() {
    this.dropdownValue = null;
    this.searchForm.reset();
    this.resetHandler.emit();
    if (!this.isReconciliation) {
      if (!this.hasMultipleMandates && this.searchForm.controls['mandantId']) {
        this.searchForm.controls['mandantId'].setValue(this.mandantId);
      } else {
        this.mandantDropdown = null;
      }
    }
  }

  search() {
    if (this.isSearchEnabled) {
      const searchParams: ProviderSearchParams = this.searchForm.value;
      this.userService.partnerCode$.subscribe((code) => {
        searchParams['practitioner.subject-group'] = code;
        searchParams['_count'] = '1000';
        searchParams['_getpageoffset'] = '1000';
        searchParams['practitioner.active'] = 'true';
        searchParams['organization.active'] = 'true';
        this.searchHandler.emit(searchParams);
      });
    }
  }
}
